import { PlusOutlined } from "@ant-design/icons";
import ObjectSelect from "@components/ObjectSelect";
import TelegramLinkCard from "@components/TelegramLinkCard";
import type { DeviceObject } from "@features/devices/api";
import type { AdminUser } from "@features/users/api";
import type { FormInstance } from "antd";
import { Alert, Button, Card, Form, Input, Select, Space, Tag } from "antd";
import dayjs from "dayjs";
import { memo } from "react";
import type { FormValues } from "../utils/userPayload";

interface UserFormFieldsProps {
  form: FormInstance<FormValues>;
  profileMode: boolean;
  profileData?: AdminUser | null;
  profileReadOnly: boolean;
  isNew: boolean;
  objects: DeviceObject[];
  objectsLoading: boolean;
  onDirtyChange: (dirty: boolean) => void;
  suppressDirtyRef: React.RefObject<boolean>;
}

const statusOptions = [
  { value: "pending", label: "Ожидает" },
  { value: "active", label: "Активен" },
  { value: "blocked", label: "Заблокирован" },
];

const roleOptions = [
  { value: "admin", label: "Администратор" },
  { value: "user", label: "Пользователь" },
  { value: "viewer", label: "Просмотр" },
];

export const UserFormFields = memo(function UserFormFields({
  form,
  profileMode,
  profileData,
  profileReadOnly,
  isNew,
  objects,
  objectsLoading,
  onDirtyChange,
  suppressDirtyRef,
}: UserFormFieldsProps) {
  const allowAccessToggle = !profileMode;
  const allowObjectBinding = !profileMode;
  const showNoteField = !profileMode;
  const requireContacts = isNew || profileMode;
  const requireName = isNew || profileMode;

  return (
    <Card>
      <Form
        layout="vertical"
        form={form}
        initialValues={{ id: "", is_active: true, objects: [] }}
        autoComplete="off"
        disabled={profileReadOnly}
        onValuesChange={() => {
          if (suppressDirtyRef.current) return;
          onDirtyChange(true);
        }}
      >
        <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
          <Form.Item
            name="id"
            label="ID пользователя"
            style={{ flex: 1, minWidth: 220 }}
            tooltip="ID генерируется автоматически (UUID)"
          >
            <Input disabled />
          </Form.Item>
          {allowAccessToggle ? (
            <>
              <Form.Item
                name="role"
                label="Роль"
                style={{ minWidth: 200 }}
                tooltip="Роль определяет уровень доступа: администратор (полный доступ), пользователь (редактирование своих объектов), просмотр (только чтение)"
              >
                <Select options={roleOptions} />
              </Form.Item>
              <Form.Item name="status" label="Статус" style={{ minWidth: 200 }}>
                <Select options={statusOptions} />
              </Form.Item>
            </>
          ) : (
            <Tag color="blue" style={{ alignSelf: "flex-end" }}>
              {form.getFieldValue("status") || profileData?.status || "pending"}
            </Tag>
          )}
        </Space>

        <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
          <Form.Item name="last_name" label="Фамилия" style={{ flex: 1, minWidth: 200 }}>
            <Input placeholder="Фамилия" />
          </Form.Item>
          <Form.Item
            name="first_name"
            label="Имя"
            style={{ flex: 1, minWidth: 200 }}
            rules={requireName ? [{ required: true, message: "Укажите имя" }] : undefined}
          >
            <Input placeholder="Имя" />
          </Form.Item>
          <Form.Item name="middle_name" label="Отчество" style={{ flex: 1, minWidth: 200 }}>
            <Input placeholder="Отчество" />
          </Form.Item>
        </Space>

        <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
          <Form.Item
            name="email"
            label="Email"
            style={{ flex: 1, minWidth: 200 }}
            rules={
              requireContacts
                ? [
                    { required: true, message: "Укажите email" },
                    { type: "email", message: "Укажите корректный email" },
                  ]
                : [{ type: "email", message: "Укажите корректный email" }]
            }
          >
            <Input placeholder="user@example.com" type="email" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Телефон"
            style={{ flex: 1, minWidth: 200 }}
            rules={requireContacts ? [{ required: true, message: "Укажите телефон" }] : undefined}
          >
            <Input placeholder="+7 (900) 000-00-00" />
          </Form.Item>
        </Space>

        <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
          <Form.Item name="company" label="Компания" style={{ flex: 1, minWidth: 200 }}>
            <Input placeholder="Организация" />
          </Form.Item>
          <Form.Item name="city" label="Город" style={{ flex: 1, minWidth: 200 }}>
            <Input placeholder="Город" />
          </Form.Item>
          <Form.Item name="address" label="Адрес" style={{ flex: 1, minWidth: 200 }}>
            <Input placeholder="Адрес" />
          </Form.Item>
        </Space>

        <Space orientation="horizontal" size="large" style={{ marginBottom: 12 }} wrap>
          <Tag color="default">
            Последний вход:{" "}
            {profileData?.last_login_at
              ? dayjs(profileData.last_login_at).format("DD.MM.YYYY HH:mm")
              : "—"}
          </Tag>
          {!profileMode && profileData?.telegram_id ? (
            <Tag color="blue">
              Telegram: {profileData.telegram_username || `id:${profileData.telegram_id}`}
            </Tag>
          ) : null}
        </Space>

        {profileMode ? (
          <TelegramLinkCard
            telegramId={profileData?.telegram_id}
            telegramUsername={profileData?.telegram_username}
          />
        ) : null}

        {showNoteField ? (
          <Form.Item name="note" label="Примечание" style={{ maxWidth: 640 }}>
            <Input.TextArea rows={3} placeholder="Свободный комментарий" />
          </Form.Item>
        ) : null}

        {allowObjectBinding ? (
          <Form.Item label="Связанные объекты">
            <Alert
              type="info"
              showIcon
              title="Можно привязать несколько объектов: пользователи в списке фильтруются по этой связи."
              style={{ marginBottom: 16 }}
            />
            <Form.List name="objects">
              {(fields, { add, remove }) => (
                <Space orientation="vertical" style={{ width: "100%" }}>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" style={{ width: "100%" }}>
                      <Form.Item
                        {...field}
                        rules={[{ required: true, message: "Выберите объект" }]}
                        style={{ flex: 1 }}
                      >
                        <ObjectSelect
                          objects={objects}
                          loading={objectsLoading}
                          placeholder="Объект"
                          allowClear
                          style={{ minWidth: 320 }}
                        />
                      </Form.Item>
                      <Button onClick={() => remove(field.name)} danger type="link">
                        Удалить
                      </Button>
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add(undefined)} icon={<PlusOutlined />}>
                    Добавить объект
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form.Item>
        ) : null}
      </Form>
    </Card>
  );
});
